<template>
  <router-view v-slot="{ Component }">
    <transition name="fade" mode="out-in">
      <component :is="Component" />
    </transition>
  </router-view>
</template>

<script>
import { useI18n } from 'vue-i18n';

export default {
  name: 'App',
  setup() {
    const { locale } = useI18n();

    // Detect user's preferred language from browser or local storage
    const savedLanguage = localStorage.getItem('user-language');

    if (savedLanguage) {
      locale.value = savedLanguage;
    } else {
      const userLang = navigator.language || navigator.userLanguage;
      let localeToSet = 'en'; // Default to English

      if (userLang.startsWith('zh')) {
        // Check for simplified vs traditional Chinese
        if (userLang.includes('CN') || userLang.includes('SG')) {
          localeToSet = 'zh'; // Simplified Chinese
        } else if (userLang.includes('TW') || userLang.includes('HK')) {
          localeToSet = 'tw'; // Traditional Chinese
        }
      }

      localStorage.setItem('user-language', localeToSet);
      locale.value = localeToSet;
    }
  }
}
</script>

<style>
/* Design tokens */
:root {
  --navy-900: #0b1f3a;
  --navy-800: #12294d;
  --navy-700: #1a3766;
  --blue-600: #1f6feb;
  --blue-500: #3b82f6;
  --blue-50: #eef4ff;
  --orange-500: #ff7a00;
  --orange-600: #e66d00;
  --green-500: #16a34a;
  --green-50: #e8f7ee;
  --gray-900: #111827;
  --gray-700: #374151;
  --gray-500: #6b7280;
  --gray-300: #d1d5db;
  --gray-200: #e5e7eb;
  --gray-100: #f5f7fa;
  --white: #ffffff;

  --radius-sm: 8px;
  --radius-md: 12px;
  --radius-lg: 16px;
  --radius-pill: 999px;

  --shadow-sm: 0 1px 2px rgba(16, 24, 40, 0.06), 0 1px 3px rgba(16, 24, 40, 0.08);
  --shadow-md: 0 8px 24px rgba(16, 24, 40, 0.08);
  --shadow-lg: 0 20px 48px rgba(16, 24, 40, 0.14);

  --font-sans: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* Global styles */
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--gray-700);
  background: var(--white);
  line-height: 1.6;
}

body.modal-open {
  overflow: hidden;
}

img {
  max-width: 100%;
  display: block;
}

/* Shared layout helpers */
.gw-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.gw-section {
  padding: 96px 0;
}

.gw-eyebrow {
  display: inline-block;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--blue-600);
  margin-bottom: 12px;
}

.gw-section-head {
  text-align: center;
  max-width: 720px;
  margin: 0 auto 56px;
}

.gw-section-head h2 {
  font-size: 2.4rem;
  line-height: 1.2;
  font-weight: 800;
  color: var(--navy-900);
  letter-spacing: -0.02em;
  margin-bottom: 14px;
}

.gw-section-head p {
  font-size: 1.1rem;
  color: var(--gray-500);
}

/* Buttons */
.gw-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 28px;
  border-radius: var(--radius-pill);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.gw-btn:hover {
  transform: translateY(-2px);
}

.gw-btn-primary {
  background: var(--orange-500);
  color: var(--white);
  box-shadow: 0 8px 20px rgba(255, 122, 0, 0.3);
}

.gw-btn-primary:hover {
  background: var(--orange-600);
  box-shadow: 0 12px 28px rgba(255, 122, 0, 0.38);
}

.gw-btn-outline {
  background: transparent;
  color: var(--white);
  border-color: rgba(255, 255, 255, 0.4);
}

.gw-btn-outline:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: var(--white);
}

.gw-btn-ghost {
  background: var(--white);
  color: var(--blue-600);
  border-color: var(--gray-200);
}

.gw-btn-ghost:hover {
  border-color: var(--blue-600);
  box-shadow: var(--shadow-md);
}

/* Scroll reveal (see v-reveal directive in main.js) */
.reveal {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.reveal.is-visible {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .reveal {
    opacity: 1;
    transform: none;
    transition: none;
  }
  html {
    scroll-behavior: auto;
  }
}

@media (max-width: 768px) {
  .gw-section {
    padding: 64px 0;
  }
  .gw-section-head h2 {
    font-size: 1.9rem;
  }
}

/* Page transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
